import React, { useLayoutEffect, useRef } from 'react';
import ScrollMagic from 'scrollmagic';
import { gsap, Linear } from 'gsap';
import {
  StyledBox,
  BlogCenterTextPairing,
  StyledButtonBox,
  InfoWrapper,
  InfoColumnLeft,
  InfoColumnRight,
} from './ChooseExperiencesSection.styles';
import { CloudImage } from '@components/CloudImage';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useIsMounted } from '@hooks/useIsMounted';
import { useTheme } from '@styledComponents';
import { GetStartedButton } from '@components/GetStartedButton/GetStartedButton';
import { useTranslation } from 'react-i18next';

interface ChooseExperiencesSectionProps {}

export const ChooseExperiencesSection: React.FC<ChooseExperiencesSectionProps> = () => {
  const infoLeftRef = useRef<HTMLDivElement>(null);
  const runcustomizeYourCashId = 'Real-time_RSVP_Controls';
  const customizeYourCashContainerRef = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const theme = useTheme();
  const { t } = useTranslation('cashFundRegistry');
  const publicCatalogSection = t('chooseExperiencesSection');

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let cashFundTimeline: ReturnType<typeof gsap.timeline>;

    if (scrollController && isMounted) {
      const initTimeline = () => {
        if (scrollController) {
          cashFundTimeline = gsap.timeline({ paused: true, defaults: { ease: Linear.easeNone } });
          cashFundTimeline.fromTo(
            infoLeftRef.current,
            {
              transform: 'translateY(0px)',
            },
            {
              transform: 'translateY(-110px)',
              duration: 0.5,
            },
            0
          );
        }
      };
      const initScene = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: `#${runcustomizeYourCashId}`,
          triggerHook: 'onCenter',
          duration: '100%',
        })
          .addTo(scrollController)
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            cashFundTimeline?.progress(event.progress);
          });
      };
      initTimeline();
      initScene();
    }
    const clearScene = () => {
      if (cashFundTimeline) {
        cashFundTimeline.progress(0);
        cashFundTimeline.kill();
      }
      if (scene) {
        scene.destroy(true);
      }
    };
    return clearScene;
  }, [scrollController, isMounted, theme]);

  return (
    <InfoWrapper ref={customizeYourCashContainerRef} id={runcustomizeYourCashId}>
      <InfoColumnLeft id="CustomeCashFundImg" ref={infoLeftRef}>
        <CloudImage src={publicCatalogSection.image} optimization="mild" />
      </InfoColumnLeft>
      <InfoColumnRight src="" srcMobile="cash-fund/funds-grid-mobile.jpg" optimization="aggressive">
        <StyledBox>
          <BlogCenterTextPairing
            alignment="left"
            eyebrow={publicCatalogSection.eyebrow}
            eyebrowColor="humanaDark4"
            eyebrowVariant="sectionEyeBrow20"
            eyebrowTagName="h2"
            title={publicCatalogSection.title}
            titleVariant="hed40"
            titleColor="neutra6"
            titleTagName="h2"
            body={publicCatalogSection.feature}
            bodyVariant="dek24"
            bodyColor="neutra5"
            bodyTagName="p"
          />
          <StyledButtonBox marginTop={[7, 8]} marginBottom={[0, 0]}>
            <GetStartedButton id="cashExperiencesGetStartedBtn" page={'registry'} variant="primary">
              {publicCatalogSection.getExploreExperiences}
            </GetStartedButton>
          </StyledButtonBox>
        </StyledBox>
      </InfoColumnRight>
    </InfoWrapper>
  );
};
